/**
 * Ernteprognose (#2341, #2373) — the Ertragsprognose surface: yield Plan /
 * Prognose / Ist over time per Kultur·Sorte, with an Ampel "auf Kurs / im
 * Rückstand" verdict, a Heute-centred ±1-year axis + "Heute" marker,
 * toggleable −1/−2/−3-Jahr prior-season overlays, and a per-field detail table.
 *
 * v2 (#2373) regroups the lens by Kultur·Sorte (deduped, no year rows, no
 * UUIDs) and aggregates the whole group over time — the chart shows the group's
 * current-SEASON cumulative Ist against prior seasons, the table breaks it down
 * per Schlag. #2419: the lens hides crops by DATA-PRESENCE (a crop is shown only
 * when the chart can draw it — picks, or a current-season forecast/plan), not by
 * a Brache/Wege name blocklist (retired); ``groups`` stays unfiltered so deep
 * links still resolve, and the page tri-states on the pick-presence aggregate
 * status. The lens id is a GROUP key; ``?cc=`` stays a crop_cycle id (the table
 * drills on it) and the selected group is derived from it, so a ``?cc=`` deep
 * link still resolves. All data flows through useErnteprognose — the only
 * twinService caller.
 *
 * #2601/#2610 — the page slices by HARVEST SEASON (``crop_year``), never by a
 * wall-clock Wirtschaftsjahr window. ``currentSeason`` (resolved in the hook,
 * see ``currentSeason.ts``) flows through ``seasonCurrentPicks`` — THE seam —
 * plus the presence facet, the forecast/plan predicate, the banner state machine
 * and the overlay chips together, so chart/banner/table/lens can never disagree
 * (#2413). A wall-clock window here made WF Spargel (harvest Mar–Jun) reachable
 * at NO date after the July rollover and bisected Erdbeeren in April.
 *
 * URL state: ``?cc=`` (group derivation / deep-link anchor — NOT the table
 * highlight anymore), ``?g=`` grain, ``?m=`` the Kumuliert/Einzelernte data mode
 * (#2544-B), ``?pm=`` the Linie/Säulen presentation (#2546-B — TRI-state: absent
 * means "follow ``?m=``", so unlike every other param here BOTH values are
 * written, never cleared on the default), ``?h=`` "Prognose vor N Tagen" horizon
 * overlay, ``?py=`` prior-year
 * overlays (kept DISTINCT from ``?h=`` — do not conflate), ``?d=`` the
 * click-selected bucket for the "Ausgewählter Tag" readout (#2456), ``?f=`` the
 * selected Schlag (#2459) — absent ⇒ Gesamt (aggregate, the default); a Schlag
 * narrows the chart to that one crop_cycle. ``?f=`` and ``?d=`` are ORTHOGONAL
 * (field vs day) — both coexist.
 */
import { useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router';
import { LensePanel, PageLoadOverlay, useMobileSidePanel } from '@hofsync/design-system';
import {
  useCyclePicks,
  useErnteprognose,
  useForecastVintages,
  type OutputCycleInfo,
  type PlanEntity,
} from '../../../hooks/useErnteprognose';
import { usePageLoadTelemetry } from '../../../hooks/usePageLoadTelemetry';
import { useTenantScopeId } from '../../../hooks/useTenantScopeId';
import { readStoredGroup, resolveStoredGroup, writeStoredGroup } from './groupPreference';
import {
  buildOutputChart,
  buildOutputVerdict,
  centeredYearWindow,
  forecastCoverage,
  forecastCoverageHintText,
  measureLabel,
  type ChartMode,
  type Grain,
  type OutputForecastPoint,
  type OutputPlanTarget,
} from './outputSeries';
import {
  availableHorizonChoices,
  buildHorizonForecastPoints,
  HORIZON_LADDER,
  horizonEmptyNoteText,
  maxLeadDays,
} from './horizonOverlay';
import {
  buildCropLensGroups,
  cropYearNumOf,
  firstCycleOfGroup,
  firstCycleOfMembers,
  isZeroDataGroup,
  OHNE_SORTE_SENTINEL,
  ohneSorteHintText,
  resolveRefinement,
  resolveSelectedGroup,
  visibleRefinementSubItems,
} from './lensGroups';
import { resolveBannerVerdict, resolveCycleView } from './cycleViewState';
import { buildPriorYearSeries, priorYearLabel } from './priorYearOverlay';
import { saisonLabel, seasonAxisWindow } from './currentSeason';
import { buildFieldRows, narrowMembersToField, narrowToCycle, resolveSelectedField } from './outputFieldRows';
import { todayIso } from '../../../utils/today';
import OutputSeriesChart, { type PriorYearToggle } from './OutputSeriesChart';
import { resolvePresentation, type ChartPresentation } from './outputChartSeries';
import OutputFieldTable from './OutputFieldTable';
import OutputVerdictLine from './OutputVerdictLine';
import SelectedDayReadoutPanel from './SelectedDayReadoutPanel';
import { findRowByBucket } from './selectedDayReadout';
import './ErnteprognosePage.css';

/** The PREFERRED horizon when the overlay is switched on — used verbatim when
 *  the data reaches back that far, else the longest lookback that does resolve
 *  (#2776, ``toggleHorizonDays`` below). */
const DEFAULT_HORIZON_DAYS = 30;
/** #2776 — why the control is greyed out, on the checkbox itself. */
const HORIZON_UNAVAILABLE_TITLE =
  'Kein Prognose-Rückblick möglich: für diese Kultur liegt keine Prognose vor.';

/** ``?g=`` → a valid grain; default + unknown ⇒ day (#2436 default-view: the
 *  chart opens at Tag grain on a Heute-centred ±1-year window).
 *
 *  #2544-A added ``week``: it used to fall through to ``day`` because no week
 *  bucket existed. That fall-through is now an accepted value — an intentional
 *  behaviour change, so an old ``?g=week`` deep link finally means what it says. */
export function resolveGrain(param: string | null): Grain {
  return param === 'week' || param === 'month' || param === 'year' ? param : 'day';
}

/**
 * ``?m=`` → the chart's data mode (#2544-B). Only the exact token ``einzel``
 * opts into Einzelernte; absent, empty or unknown ⇒ Kumuliert, the default view.
 *
 * ASCII, lowercase, German — the repo's URL habit (``?g=week``, ``?view=kanal``),
 * and the default is never written into the URL, so a plain ``/apps/ernteprognose``
 * link keeps meaning what it always did.
 */
export function resolveChartMode(param: string | null): ChartMode {
  return param === 'einzel' ? 'perPeriod' : 'cumulative';
}

/** ``?h=`` → a horizon on ``HORIZON_LADDER``; absent/unknown ⇒ null (overlay
 *  off). #2776 extended the ladder DOWNWARD ({7,14,30,60,90}) — a fixed
 *  whitelist, never the pool's own max lead, so a deep link keeps meaning the
 *  same thing as the tenant's data changes. Which of them the UI OFFERS is a
 *  separate, per-pool question (``availableHorizonChoices``). */
export function resolveHorizonDays(param: string | null): number | null {
  const n = param == null ? NaN : Number(param);
  return HORIZON_LADDER.includes(n) ? n : null;
}

/** ``?py=`` → the enabled prior-year offsets ⊆ {1,2,3}; absent ⇒ none. */
export function resolvePriorYears(param: string | null): number[] {
  if (!param) return [];
  const out = new Set<number>();
  for (const tok of param.split(',')) {
    const n = Number(tok.trim());
    if (n === 1 || n === 2 || n === 3) out.add(n);
  }
  return [...out].sort();
}

/**
 * Crop-total page header (#2408-B): "<Kultur> — N Schläge", N = ALL current-WJ
 * members of the crop group. Stays constant under ANY refinement so a farmer
 * can never read a refined subset (or the untagged remainder) as the crop
 * total. Zero current members (planning-only / history-only crops) omit the
 * suffix — the banner surface explains those states.
 */
export function cropTotalHeader(label: string, currentWjCount: number): string {
  if (currentWjCount <= 0) return label;
  return `${label} — ${currentWjCount} ${currentWjCount === 1 ? 'Schlag' : 'Schläge'}`;
}

/**
 * #2601-B — the WJ-start disclosure hint. Rendered whenever ``wjStartMonth`` came
 * from the built-in default rather than the tenant's ``liquiditaet_config`` fact
 * (WF prod has no such fact), in the existing hint style. The season rule means a
 * missing config no longer HIDES harvest, but it still decides which season is
 * current at the year boundary — so the assumption is disclosed, never silent.
 */
const MONTH_NAMES_DE = [
  'Januar',
  'Februar',
  'März',
  'April',
  'Mai',
  'Juni',
  'Juli',
  'August',
  'September',
  'Oktober',
  'November',
  'Dezember',
];

export function wjStartHintText(month: number): string {
  const name = MONTH_NAMES_DE[month - 1] ?? String(month);
  // "kein GÜLTIGES" rather than "kein": the default is also used when a
  // liquiditaet_config fact EXISTS but carries an out-of-range wj_start_month,
  // where claiming none is hinterlegt would be false.
  return `Wirtschaftsjahr ab ${name} (Standard) — für diesen Betrieb ist kein gültiges Wirtschaftsjahr hinterlegt.`;
}

/** Human label for a crop cycle: crop + variety, with the crop year as a suffix. */
export function cycleLabel(cycle: OutputCycleInfo): string {
  const head = [cycle.crop, cycle.variety].filter(Boolean).join(' ') || cycle.cropCycleId;
  return cycle.cropYear ? `${head} · ${cycle.cropYear}` : head;
}

/** The plan target for a cycle: the most recently-planned CURRENT-WJ revision, or
 *  null (grey the line). Plans whose ``planPeriod`` is null or ≠ ``currentSeason`` are
 *  dropped BEFORE the latest-revision reduce, mirroring the lens predicate
 *  (``useErnteprognose.ts:303``: ``planPeriod != null && Number(planPeriod) === currentSeason``)
 *  so the chart never draws a Plan line for a cycle the lens hides (#2433 / #2419). */
export function pickPlanTarget(plans: PlanEntity[], currentSeason: number): OutputPlanTarget | null {
  const withValue = plans.filter(
    (p) => p.value != null && p.planPeriod != null && Number(p.planPeriod) === currentSeason,
  );
  if (withValue.length === 0) return null;
  const latest = withValue.reduce((a, b) => ((b.asPlannedAt ?? '') > (a.asPlannedAt ?? '') ? b : a));
  return { value: latest.value as number, measureKind: latest.measureKind, planPeriod: latest.planPeriod };
}

/** The prior-year offset of a crop_cycle relative to the current WJ (0 = current);
 *  null-cropYear cycles have NO offset — they stay group members but are excluded
 *  from current/prior/future detection (#2408). */
const offsetOf = (cycle: OutputCycleInfo, currentSeason: number): number | null => {
  const y = cropYearNumOf(cycle);
  return y == null ? null : currentSeason - y;
};

export default function ErnteprognosePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  // HOF-106 — which tenant's remembered crop lens applies. Withheld
  // (``resolved`` false) while auth is in flight; the default-``?cc=`` stamp
  // below is gated on it for the reason its own comment gives. Mechanics are
  // HOF-101's, reused as built (see ``groupPreference``).
  const { tenantId, resolved: tenantResolved } = useTenantScopeId();
  // Read once per tenant, not in a lazy ``useState`` initializer: the tenant
  // arrives with ``/auth/me``, so its namespace is not known at mount, and
  // re-reading when the scope changes is what makes „Anzeige als" hand over the
  // right farm's crop.
  const storedGroupRaw = useMemo(
    () => (tenantResolved ? readStoredGroup(tenantId) : null),
    [tenantResolved, tenantId],
  );
  const ccParam = searchParams.get('cc');
  const grain = resolveGrain(searchParams.get('g'));
  // #2544-B — Kumuliert (running total) vs Einzelernte (per-bucket amount). A
  // DATA axis: it selects which row fields the chart plots, nothing else.
  const mode = resolveChartMode(searchParams.get('m'));
  // #2546-B — Linie vs Säulen, the orthogonal PRESENTATION axis. Resolved here
  // and passed down already-decided, because the resolution is TRI-state: the
  // default depends on ``mode``, so an absent ``?pm=`` re-derives on every mode
  // switch while an explicit one survives it. See ``resolvePresentation``.
  const presentation = resolvePresentation(searchParams.get('pm'), mode);
  const horizonDays = resolveHorizonDays(searchParams.get('h'));
  const overlayEnabled = horizonDays != null;
  const priorYearsParam = useMemo(() => resolvePriorYears(searchParams.get('py')), [searchParams]);
  // #2456 Ask 4 — the click-selected bucket ("Ausgewählter Tag" readout). Raw
  // string; validated against the chart rows below (cleared when it leaves range).
  const selectedBucketParam = searchParams.get('d');
  // #2459 — the selected Schlag (a crop_cycle id). Raw; resolved against the
  // current member set below (absent or stale ⇒ Gesamt = aggregate default).
  const fParam = searchParams.get('f');

  const {
    cycles,
    forecasts,
    plans,
    wjStartMonth,
    wjStartMonthSource,
    currentSeason,
    fieldNames,
    pickCycles,
    cyclesWithCurrentForecast,
    pickPresenceStatus,
    isLoading,
    error,
  } = useErnteprognose();

  // ── Crop-level lens (v3, #2408) ──────────────────────────────────────────
  // #2601/#2610 — everything below slices on ``currentSeason`` (a ``crop_year``
  // harvest season, resolved in the hook), NOT on a wall-clock Wirtschaftsjahr
  // window. ``wjStartMonth`` survives only as that season's derivation input and
  // for ``yield_plan.plan_period`` matching; ``wjWindowOf`` is no longer consumed
  // here at all (it stays untouched for Liquiditätsplanung / Deckungsbeitrag).
  // Ernteprognose intentionally does NOT copy Deckungsbeitrag's
  // ``withoutFutureWirtschaftsjahre`` drop (#2268): future planning cycles must
  // stay group members so a ``?cc=`` deep link onto next season's register row
  // resolves to the Planung banner (#2408) instead of dead-ending. Do not
  // "harmonize" this with useDeckungsbeitrag.
  const groups = useMemo(() => buildCropLensGroups(cycles, currentSeason), [cycles, currentSeason]);
  // #2419 display-only collapse: a crop leaves the LENS unless the chart can
  // DRAW it (a current-WJ pick, a current forecast/plan, or a prior pick in the
  // −1..−3 span — see ``isZeroDataGroup``). ``groups`` itself stays UNFILTERED
  // so deep links + selection keep resolving over all groups. The "all hidden →
  // show all" guard is GONE (codex P2): the page tri-states on
  // ``pickPresenceStatus`` instead — loading → wait, error → fail-open below,
  // success-empty → an explicit empty state. Only on an aggregate FAILURE do we
  // fail open (show every group) so a lens read error never blanks the app.
  const visibleGroups = useMemo(() => {
    if (pickPresenceStatus === 'error') return groups;
    return groups.filter(
      (g) => !isZeroDataGroup(g, currentSeason, pickCycles, cyclesWithCurrentForecast),
    );
  }, [groups, currentSeason, pickPresenceStatus, pickCycles, cyclesWithCurrentForecast]);
  // A ``?cc=`` deep link into a group in the UNFILTERED ``groups`` always
  // renders (invariant #1), even when the group is hidden / all groups are
  // hidden — so it takes precedence over the empty/loading lens states.
  const hasDeepLinkGroup = useMemo(
    () => ccParam != null && groups.some((g) => g.memberIds.includes(ccParam)),
    [ccParam, groups],
  );
  // #2419 lens tri-state — driven by the pick-presence aggregate STATUS, never
  // ``data===undefined`` (true during BOTH load and error). A ``?cc=`` deep link
  // into any group overrides all three (invariant #1: it always renders).
  // Loading → a placeholder, never every zero-data crop. Success + zero visible
  // → an explicit empty state (no un-hide). Error → ``visibleGroups`` already
  // fails open (every group shown); the render adds a soft notice. Defined here
  // (above the default-``?cc=`` effect) so the auto-stamp can be gated on
  // ``showLensContent`` — otherwise stamping a default cc would masquerade as a
  // deep link and bypass the loading/empty states.
  const showPickLoading = pickPresenceStatus === 'loading' && !hasDeepLinkGroup;
  const showEmptyLens = pickPresenceStatus === 'success' && visibleGroups.length === 0 && !hasDeepLinkGroup;
  const showLensNotice = pickPresenceStatus === 'error';
  const showLensContent = !showPickLoading && !showEmptyLens;
  // HOF-106 — the remembered crop, validated against the VISIBLE groups (#2419's
  // data-presence filter): a crop the chart cannot draw this season has left the
  // lens, and restoring it would open a view with nothing in it. The unfiltered
  // ``groups`` set is deliberately not the test — it exists so deep links keep
  // resolving, and a preference is not a deep link.
  const preferredGroupKey = useMemo(
    () => resolveStoredGroup(storedGroupRaw, visibleGroups),
    [storedGroupRaw, visibleGroups],
  );
  // Precedence: an explicit ``?cc=`` (a statement about THIS visit) beats the
  // stored group, which beats first-visible. A parameter that is PRESENT but
  // resolves to nothing still suppresses the preference and falls through to
  // first-visible — HOF-101's own semantics (``katParam || storedKatId``), kept
  // identical so the two pages cannot drift on what "absent" means.
  const selectedGroupKey = useMemo(() => {
    if (!ccParam && preferredGroupKey) return preferredGroupKey;
    return resolveSelectedGroup(ccParam, groups, visibleGroups);
  }, [ccParam, preferredGroupKey, groups, visibleGroups]);
  const selectedGroup = useMemo(
    () => groups.find((g) => g.groupKey === selectedGroupKey) ?? null,
    [groups, selectedGroupKey],
  );

  // ── ?s= variety refinement (#2408-B) ─────────────────────────────────────
  // Precedence on inconsistent URLs: ``?cc=`` determines the group, ``?s=``
  // SOLELY determines the refinement (resolved inside the cc-derived group;
  // unknown values degrade to crop total). ``?cc=`` alone never auto-refines.
  const sParam = searchParams.get('s');
  // #2435 — the presence Sets the LEVEL-2 variety DISPLAY filter consumes (the
  // rendered sub-item axis ``lensItems`` + the click handler ``handleSelectLens``;
  // deep-link ``?s=`` resolution runs unfiltered per #2454 and needs none of
  // this). On a pick-presence aggregate ERROR the level-1 lens fails open
  // (``visibleGroups`` shows every group); the level-2 variety axis must fail
  // open in lockstep, or a pick-only Sorte would be treated as dataless and its
  // sub-item would vanish. A match-all predicate makes every member drawable,
  // exactly mirroring ``visibleGroups``' error branch. (Loading is fine: the
  // whole lens is gated behind ``showLensContent`` while loading.)
  const refinementPresence = useMemo(() => {
    if (pickPresenceStatus === 'error') {
      const all = { has: () => true } as unknown as ReadonlySet<string>;
      return { pick: all, currentForecast: all };
    }
    return { pick: pickCycles, currentForecast: cyclesWithCurrentForecast };
  }, [pickPresenceStatus, pickCycles, cyclesWithCurrentForecast]);
  // #2454 deep-link invariant #1 — resolve ``?s=`` against the UNFILTERED axis
  // (not the data-presence-filtered display axis): a ``?s=`` into a variety
  // #2435 hides for lack of data STILL resolves and renders its (honest, maybe
  // empty) view, mirroring how ``?cc=`` resolves over the unfiltered ``groups``
  // at level 1. Only the lens DISPLAY is filtered; deep-link selection is not.
  const refinement = useMemo(() => resolveRefinement(sParam, selectedGroup), [sParam, selectedGroup]);
  // The member subset everything VISIBLE (chart/table/overlays/plan/picks)
  // computes from: the refined subset when a refinement is active, else the
  // whole crop. The crop-total header + lens counts stay group-scoped.
  const activeMembers = useMemo(
    () => refinement?.members ?? selectedGroup?.members ?? [],
    [refinement, selectedGroup],
  );

  const currentSeasonMembers = useMemo(
    () => activeMembers.filter((m) => offsetOf(m, currentSeason) === 0),
    [activeMembers, currentSeason],
  );
  const priorYearMembers = useMemo(
    () =>
      activeMembers.filter((m) => {
        const o = offsetOf(m, currentSeason);
        return o != null && o >= 1 && o <= 3;
      }),
    [activeMembers, currentSeason],
  );
  const currentMemberIds = useMemo(() => currentSeasonMembers.map((m) => m.cropCycleId), [currentSeasonMembers]);

  // #2459 — the resolved Schlag selection: ``?f=`` only holds while its cycle is
  // still a current member; absent or stale ⇒ null = Gesamt (aggregate default).
  // The TABLE now highlights on THIS (not ``?cc=``, which stays the group anchor).
  const selectedFieldId = useMemo(
    () => resolveSelectedField(fParam, currentMemberIds),
    [fParam, currentMemberIds],
  );
  const fieldSelected = selectedFieldId != null;
  // #2505 — the selected Schlag's actual ``field_id`` (``selectedFieldId`` is a
  // crop_cycle id — #2459 naming): the only join key STABLE across crop_years
  // (each year is a different crop_cycle id), so it is what scopes the
  // prior-year overlay to the field. Null when Gesamt is active OR the selected
  // cycle has no Schlag — an "Ohne Schlag" cycle cannot join prior years.
  const selectedSchlagFieldId = useMemo(
    () =>
      selectedFieldId == null
        ? null
        : activeMembers.find((m) => m.cropCycleId === selectedFieldId)?.fieldId ?? null,
    [selectedFieldId, activeMembers],
  );
  // #2459 (codex P2) — the horizon overlay ("Prognose vor N Tagen") is a
  // GROUP-level view: its vintage pool is fetched group/refinement-wide and
  // carries no crop_cycle, so it cannot be field-scoped without re-plumbing the
  // fetch. In a field view it is HIDDEN — and, gated below, its vintage fetch /
  // horizon build / truncation warning are all skipped (no group fan-out, no
  // warning for an overlay the UI says is unavailable). Per-field is a follow-up.
  const showHorizonOverlay = overlayEnabled && !fieldSelected;

  // #2419 — pick-aware: a prior offset is offered ONLY when a member at that
  // offset actually has picks in the −1..−3 span (``pickCycles``), i.e. what
  // ``buildPriorYearSeries`` can draw. Drives both the top-right toggles and the
  // ``overlayOffer`` below, so a pick-less prior year offers no toggle/curve
  // (fixes "offers Vorjahres-Kurven that draw nothing"). Same span signal the
  // lens filter uses → lens + banner + chart agree.
  const availableOffsets = useMemo(
    () =>
      [
        ...new Set(
          priorYearMembers.filter((m) => pickCycles.has(m.cropCycleId)).map((m) => offsetOf(m, currentSeason)),
        ),
      ]
        .filter((o): o is number => o != null && o >= 1 && o <= 3)
        .sort(),
    [priorYearMembers, pickCycles, currentSeason],
  );
  // #2505 — the offsets OFFERED in a field view: the same pick-aware predicate,
  // further narrowed to the selected Schlag's own prior cycles (field_id join).
  // Only the toggle OFFER narrows — ``enabledOffsets`` below (?py= ∩ GROUP
  // availability) must stay group-level: it also scopes the prior-pick fetch
  // and the Gesamt aggregate, which a field selection must not perturb.
  const fieldAvailableOffsets = useMemo(
    () =>
      [
        ...new Set(
          narrowMembersToField(priorYearMembers, selectedSchlagFieldId)
            .filter((m) => pickCycles.has(m.cropCycleId))
            .map((m) => offsetOf(m, currentSeason)),
        ),
      ]
        .filter((o): o is number => o != null && o >= 1 && o <= 3)
        .sort(),
    [priorYearMembers, selectedSchlagFieldId, pickCycles, currentSeason],
  );
  const enabledOffsets = useMemo(
    () => priorYearsParam.filter((o) => availableOffsets.includes(o)),
    [priorYearsParam, availableOffsets],
  );
  // Prior-pick fetches stay lazy PER ENABLED OFFSET (#2408): a crop-level
  // Erdbeeren group carries ~90 prior members across offsets 1..3 — fetch only
  // the toggled-on years' cycles, not the whole 1..3 pool.
  const enabledPriorMembers = useMemo(
    () =>
      priorYearMembers.filter((m) => {
        const o = offsetOf(m, currentSeason);
        return o != null && enabledOffsets.includes(o);
      }),
    [priorYearMembers, currentSeason, enabledOffsets],
  );
  const enabledPriorMemberIds = useMemo(
    () => enabledPriorMembers.map((m) => m.cropCycleId),
    [enabledPriorMembers],
  );

  const setParams = useCallback(
    (updates: Record<string, string | null>) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        for (const [key, value] of Object.entries(updates)) {
          if (value === null) next.delete(key);
          else next.set(key, value);
        }
        return next;
      });
    },
    [setSearchParams],
  );

  // Persist a default ?cc= once (deep-linkable): the selected group's first
  // current-WJ field-cycle, so the group is derivable and the table drills.
  // Refinement-aware (#2408-B): while refined, ``?cc=`` must live INSIDE the
  // refined subset — a cc outside it (inconsistent URL) loses to ``?s=`` and is
  // re-stamped to the subset's deterministic first member.
  const defaultCc = useMemo(
    () => (selectedGroup ? firstCycleOfGroup(selectedGroup, currentSeason) : null),
    [selectedGroup, currentSeason],
  );
  const refinementCc = useMemo(
    () =>
      refinement && !refinement.members.some((m) => m.cropCycleId === ccParam)
        ? firstCycleOfMembers(refinement.members, currentSeason)
        : null,
    [refinement, ccParam, currentSeason],
  );
  useEffect(() => {
    if (isLoading) return;
    // #2419 — don't auto-stamp a default ?cc= while the lens is showing a
    // loading placeholder or the empty-data state; a stamp there would read as a
    // deep link and force zero-data content. A GENUINE ?cc= in the URL still
    // renders (it makes ``showLensContent`` true via ``hasDeepLinkGroup``).
    if (!showLensContent) return;
    // HOF-106 — and equally, not before the TENANT is known. The preference
    // cannot be read until ``/auth/me`` lands, and stamping the first visible
    // group in the meantime writes a ``?cc=`` that ``hasDeepLinkGroup`` then
    // reads as an explicit deep link: the arriving preference would lose to the
    // page's own guess, on every cold visit. ``ProtectedLayout`` already holds
    // the route until auth settles, so in the app this gate rarely fires; it is
    // what keeps the precedence a property of the page.
    if (!tenantResolved) return;
    const target = refinementCc ?? (ccParam ? null : defaultCc);
    if (!target) return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('cc', target);
        return next;
      },
      { replace: true },
    );
  }, [ccParam, isLoading, showLensContent, tenantResolved, defaultCc, refinementCc, setSearchParams]);

  const pageLoadOverlay = usePageLoadTelemetry({
    page: 'ernteprognose',
    view: `ernteprognose:g=${selectedGroup?.label ?? 'none'}:grain=${grain}:mode=${mode}`,
    isLoading,
  });

  const mobilePanel = useMobileSidePanel();

  // Lens rows (#2408-B): variety refinements render as LensePanel sub-items —
  // only when the group has ≥2 distinct refinements, with the ohne-Sorte
  // remainder labelled explicitly, never blank. Counts are current-WJ counts
  // (0 → no badge). #2435 — the sub-item axis is DATA-PRESENCE-filtered
  // (``visibleRefinementSubItems``): a variety with no current-WJ pick / no span
  // pick / no current forecast is hidden (mirroring #2419 at level 1), so a crop
  // whose only data-carrying refinement is the remainder collapses to no axis.
  const lensItems = useMemo(
    () =>
      visibleGroups.map((g) => {
        const subs = visibleRefinementSubItems(
          g,
          currentSeason,
          refinementPresence.pick,
          refinementPresence.currentForecast,
        );
        return {
          id: g.groupKey,
          name: g.label,
          count: g.currentWjCount || undefined,
          children:
            subs.length > 0
              ? subs.map((r) => ({ id: r.id, name: r.label, count: r.currentWjCount || undefined }))
              : undefined,
        };
      }),
    [visibleGroups, currentSeason, refinementPresence],
  );

  // ── Chart series (group-aggregated over the fixed WJ window) ──────────────
  // #2775-R2 — ONE drawable-forecast derivation, in two stages. Stage 1
  // (``memberForecasts``): the member-scoped, dated + valued superset the axis
  // window validates its candidate dates from. Stage 2 (``drawnForecasts``,
  // below the axis memo — it needs the resolved window): the points the chart
  // can actually DRAW — finite value, date inside the axis spine. Stage 2 feeds
  // the chart, the coverage hint AND the Schläge table, so the three surfaces
  // on this screen can never disagree about which Schlag has a Prognose (a
  // date-less, year-typo'd or NaN forecast is drawable nowhere, so it counts
  // nowhere).
  const memberForecasts = useMemo(
    () =>
      forecasts.filter(
        (f) => f.cropCycleId != null && currentMemberIds.includes(f.cropCycleId) && f.forecastDate != null && f.value != null,
      ),
    [forecasts, currentMemberIds],
  );

  const forecastEntities = useMemo(
    () =>
      forecasts
        .filter((f) => f.cropCycleId != null && currentMemberIds.includes(f.cropCycleId))
        .map((f) => ({ forecastId: f.forecastId, forecastDate: f.forecastDate })),
    [forecasts, currentMemberIds],
  );

  const plan = useMemo<OutputPlanTarget | null>(() => {
    let value = 0;
    let any = false;
    let mk: string | null | undefined;
    for (const m of currentSeasonMembers) {
      const t = pickPlanTarget(plans.filter((p) => p.cropCycleId === m.cropCycleId), currentSeason);
      if (t) {
        value += t.value;
        any = true;
        mk = mk ?? t.measureKind;
      }
    }
    return any ? { value, measureKind: mk } : null;
  }, [currentSeasonMembers, plans, currentSeason]);

  const { picks: currentPicks } = useCyclePicks(currentMemberIds);
  const { picks: priorPicks } = useCyclePicks(enabledPriorMemberIds, enabledOffsets.length > 0);
  // Cache key = the refinement id while refined (namespaced — can never collide
  // with a group key), else the crop-level group key: the vintage pool follows
  // the ACTIVE member subset, so the key must too (#2408-B).
  const { vintages, isLoading: vintagesLoading, truncated: vintagesTruncated } = useForecastVintages(
    refinement?.id ?? selectedGroupKey,
    forecastEntities,
    // #2459 (codex P2) — skip the group-wide vintage fetch in a field view.
    showHorizonOverlay,
  );

  const measureKind = plan?.measureKind ?? memberForecasts[0]?.measureKind ?? 'harvest_kg';
  const unit = measureLabel(measureKind);

  const horizonPoints = useMemo(
    () => (showHorizonOverlay && horizonDays != null ? buildHorizonForecastPoints(vintages, horizonDays) : undefined),
    [showHorizonOverlay, horizonDays, vintages],
  );

  const todayYm = todayIso().slice(0, 7);

  // ══ THE SEAM (#2413 invariant, re-anchored by #2601-A / #2610) ════════════
  // Slice the current season's Ist ONCE, then feed the SAME list to the
  // chart/banner AND the Schläge table so they can never disagree on what counts
  // as this season's harvest — in ANY grain.
  //
  // The slice is SEASON membership, not a date window: a pick belongs to the
  // current season iff its crop_cycle's ``crop_year === currentSeason``, and the
  // season's Ist is that membership's FULL pick span at whatever wall-clock date
  // it carries. The previous WJ month-window clamp here is exactly what broke
  // both issues — it hid WF Spargel entirely (harvest Mar–Jun, no wall-clock
  // date inside a Jul→Jun window) and bisected Erdbeeren in April. Dropping it
  // costs nothing in correctness: ``currentPicks`` is fetched BY the current
  // season's member ids, so the filter below only restates that membership
  // (defensively, against a stale fetch) rather than re-cutting by time.
  //
  // Seasons partition the picks — one pick, one cycle, one ``crop_year`` — so a
  // season crossing the WJ boundary is counted once, whole, in its own season.
  const currentMemberIdSet = useMemo(() => new Set(currentMemberIds), [currentMemberIds]);
  const seasonCurrentPicks = useMemo(
    () => currentPicks.filter((p) => p.cropCycleId != null && currentMemberIdSet.has(p.cropCycleId)),
    [currentPicks, currentMemberIdSet],
  );

  const priorSeries = useMemo(() => {
    if (enabledOffsets.length === 0) return [];
    const cropYearByCycle: Record<string, number> = {};
    for (const m of enabledPriorMembers) {
      const y = cropYearNumOf(m);
      if (y != null) cropYearByCycle[m.cropCycleId] = y;
    }
    return buildPriorYearSeries(
      priorPicks.map((p) => ({ cropCycleId: p.cropCycleId, date: p.date, qty: p.qty })),
      cropYearByCycle,
      currentSeason,
    ).filter((s) => enabledOffsets.includes(s.offset));
  }, [enabledOffsets, enabledPriorMembers, priorPicks, currentSeason]);

  // #2505 — the FIELD-scoped prior-year series: the SAME already-fetched
  // ``priorPicks``, re-run through ``buildPriorYearSeries`` with a
  // ``cropYearByCycle`` map narrowed to the selected Schlag's prior cycles
  // (the function drops picks of unmapped cycles) — no second fetch, and the
  // group ``priorSeries`` above stays byte-identical for the Gesamt
  // aggregate + verdict. A null ``selectedSchlagFieldId`` (Ohne Schlag)
  // narrows to an empty map ⇒ no prior curve, never a null-key merge.
  const fieldPriorSeries = useMemo(() => {
    if (selectedFieldId == null || enabledOffsets.length === 0) return [];
    const cropYearByCycle: Record<string, number> = {};
    for (const m of narrowMembersToField(enabledPriorMembers, selectedSchlagFieldId)) {
      const y = cropYearNumOf(m);
      if (y != null) cropYearByCycle[m.cropCycleId] = y;
    }
    return buildPriorYearSeries(
      priorPicks.map((p) => ({ cropCycleId: p.cropCycleId, date: p.date, qty: p.qty })),
      cropYearByCycle,
      currentSeason,
    ).filter((s) => enabledOffsets.includes(s.offset));
  }, [selectedFieldId, selectedSchlagFieldId, enabledOffsets, enabledPriorMembers, priorPicks, currentSeason]);

  // ── The chart AXIS (#2436, re-derived for the season slice by #2601/#2610) ──
  // Base: the symmetric ±1-year window centred on Heute, so the "Heute" splitter
  // sits in the middle and pan reaches the forecast horizon (today + 1y).
  //
  // The axis must CONTAIN everything the chart draws, or the seam above is undone
  // downstream: ``buildOutputChart`` emits only the buckets of ``wjBuckets(window,
  // grain)`` and clips EVERY series to them, while the Schläge table has no window
  // at all. Anything drawn but outside the spine therefore vanishes from the chart
  // AND from the banner verdict (which grades the chart rows) while the table
  // still shows it — precisely the #2413 disagreement this task exists to close.
  // That is reachable now that no series is date-clamped any more: between January
  // and ``wj_start_month`` the current season is the PREVIOUS calendar year, so an
  // early season's Mar–Jun picks — and a forecast-only cycle's spring
  // forecast_date, and the prior-year curves, which ``shiftYears`` lands in
  // calendar year ``currentSeason`` — can all sit more than 12 months from Heute.
  // So the union runs over ALL four drawn inputs, not just the Ist.
  //
  // BOUNDED + VALIDATED, because raw ``operation.date`` strings now reach the axis
  // (the old WJ clamp made that impossible): a candidate must parse as ``YYYY-MM``
  // and fall inside ``currentSeason ± 1`` — the only calendar years this chart can
  // legitimately draw. Without that, a one-digit year typo (2016 for 2026) would
  // stretch the day spine to thousands of buckets, and a non-date string would
  // reach ``lastDayOfMonth`` and throw on ``new Date(NaN).toISOString()``.
  //
  // NOTE two real consequences. (1) The chart's zoom viewport defaults to the
  // FULL spine (``useChartTimeWindow`` with no ``defaultWindow``), so when the
  // season sits outside the centred range the chart opens wider and "Heute" is no
  // longer centred — the intended trade: showing the season the farmer came for
  // beats keeping Heute centred over an empty range. (2) The window now depends
  // on ``priorSeries``, so toggling a ``?py=`` chip can change the bucket count
  // and therefore reset the user's zoom/pan (``useChartTimeWindow`` resets on its
  // count|start|end signature) — once on toggle, and again when the lazy prior
  // picks resolve. Accepted: an overlay that draws outside the spine would be
  // worse than a reset. Whenever the season sits inside the centred window (the
  // common case) the axis, and the default view, are byte-identical to #2436's.
  const axisWindow = useMemo(() => {
    // Every date the chart can draw. The GROUP-level lists are supersets of their
    // field-narrowed counterparts (``chartForecastPoints`` ⊆ ``forecastPoints``,
    // ``fieldPriorSeries`` ⊆ ``priorSeries``, ``chartActuals`` ⊆
    // ``seasonCurrentPicks``), so covering them covers the field view too — and
    // the axis stays stable when a Schlag is selected.
    const dates: (string | null | undefined)[] = [];
    for (const p of seasonCurrentPicks) dates.push(p.date);
    for (const f of memberForecasts) dates.push(f.forecastDate);
    for (const h of horizonPoints ?? []) dates.push(h.forecastDate);
    for (const s of priorSeries) for (const pt of s.points) dates.push(pt.date);
    return seasonAxisWindow(centeredYearWindow(todayYm), currentSeason, dates);
  }, [todayYm, currentSeason, seasonCurrentPicks, memberForecasts, horizonPoints, priorSeries]);

  // #2775-R2 — stage 2 of the drawable-forecast derivation (see above): finite
  // value + date inside the resolved axis spine (month-slice inclusion — every
  // grain's spine covers the window's whole months). Non-circular: the axis
  // validates its candidates itself, so it reads the stage-1 superset.
  const drawnForecasts = useMemo(
    () =>
      memberForecasts.filter((f) => {
        const ym = (f.forecastDate as string).slice(0, 7);
        return Number.isFinite(f.value as number) && ym >= axisWindow.startYm && ym <= axisWindow.endYm;
      }),
    [memberForecasts, axisWindow],
  );
  const forecastPoints = useMemo<OutputForecastPoint[]>(
    () =>
      // seriesId (crop_cycle) lets the aggregate seed each member's Prognose
      // from its own Ist before summing (#2455 — staggered forecast starts).
      drawnForecasts.map((f) => ({
        forecastDate: f.forecastDate as string,
        value: f.value as number,
        measureKind: f.measureKind,
        seriesId: f.cropCycleId,
      })),
    [drawnForecasts],
  );

  // ── #2776 — which horizons the control may OFFER ──────────────────────────
  // The #2419 ``availableOffsets`` rule, applied to the lookback: offer only
  // what the data can actually draw. WF prod has ONE forecast Stand with a
  // 23-day max lead, so fixed 30/60/90 choices drew an empty chart at every
  // setting — no line, no number, no reason.
  //
  // TWO signals, deliberately:
  //  · EAGER (pre-toggle, zero extra fetches) — the DRAWN forecasts' own
  //    ``generated_at`` (same set the chart's Prognose comes from, #2775-R2, so
  //    a point that draws nowhere offers nothing either). The revision pool is
  //    lazy — before the checkbox is ticked there is nothing else to ask. The
  //    current-state feed carries only each forecast's LATEST revision, so this
  //    is a conservative LOWER bound on the true max lead; it is sound in the
  //    direction that matters, since a lead ≥ N guarantees
  //    ``selectVintageAtHorizon`` resolves that day at N.
  //  · EXACT (post-toggle) — the fetched pool, which sees the older revisions
  //    the eager bound cannot. It only ever WIDENS the offer (max of the two),
  //    so an option can never vanish under the cursor mid-session, and it is
  //    what the empty-overlay note reasons about.
  const eagerMaxLead = useMemo(() => maxLeadDays(drawnForecasts), [drawnForecasts]);
  const exactMaxLead = useMemo(
    () => (showHorizonOverlay ? maxLeadDays(vintages) : null),
    [showHorizonOverlay, vintages],
  );
  const offeredHorizons = useMemo(
    () => availableHorizonChoices(eagerMaxLead, HORIZON_LADDER),
    [eagerMaxLead],
  );
  const exactHorizons = useMemo(
    () => availableHorizonChoices(exactMaxLead, HORIZON_LADDER),
    [exactMaxLead],
  );
  const selectableHorizons = useMemo(() => {
    const widest =
      eagerMaxLead == null || exactMaxLead == null ? eagerMaxLead ?? exactMaxLead : Math.max(eagerMaxLead, exactMaxLead);
    return availableHorizonChoices(widest, HORIZON_LADDER);
  }, [eagerMaxLead, exactMaxLead]);
  // Ticking the checkbox stamps a horizon that RESOLVES: the preferred 30-day
  // default when the data reaches that far, else the LONGEST lookback it does
  // support (WF: 14). Doubles as the closed control's displayed value, so the
  // select previews what ticking would do rather than a dead 30.
  const toggleHorizonDays = offeredHorizons.includes(DEFAULT_HORIZON_DAYS)
    ? DEFAULT_HORIZON_DAYS
    : offeredHorizons[offeredHorizons.length - 1] ?? DEFAULT_HORIZON_DAYS;
  // A togglable dead overlay is worse than a greyed-out one WITH a reason — but
  // DENYING the overlay needs proof, and ``eagerMaxLead`` is only a LOWER bound
  // (it sees each forecast's latest revision only). A tenant whose latest
  // revisions were issued late can still hold OLDER ones that resolve, and a
  // disabled checkbox would never fetch the pool to find out — the overlay would
  // be unreachable rather than merely unoffered (codex P2). So a short eager
  // lead greys out ladder STEPS (recoverable — the exact pool widens them) but
  // never the checkbox; only having nothing to fetch at all is proof. Gates
  // switching ON only: a deep-linked ``?h=`` stays on and is explained by the
  // note instead — the URL is never silently rewritten.
  const horizonUnavailable = !overlayEnabled && forecastEntities.length === 0;

  const rows = useMemo(
    () =>
      buildOutputChart({
        actuals: seasonCurrentPicks.map((p) => ({ date: p.date, qty: p.qty, unit: p.unit, seriesId: p.cropCycleId })),
        forecasts: forecastPoints,
        plan,
        grain,
        horizonPoints,
        window: axisWindow,
        priorYears: priorSeries,
      }),
    [seasonCurrentPicks, forecastPoints, plan, grain, horizonPoints, axisWindow, priorSeries],
  );

  // The GROUP aggregate verdict — the Ampel banner (and crop ``<h1>``) stay
  // group-level even when a Schlag is selected (#2459 out-of-scope): the verdict
  // always reads ``rows`` (the aggregate), never the narrowed ``chartRows``.
  const verdict = useMemo(() => buildOutputVerdict(rows, unit), [rows, unit]);

  // #2775 — how much of the harvest the aggregate Prognose covers. Inputs are
  // the DRAWN forecast points (R2: date-less / out-of-axis / NaN points count
  // nowhere) and the same picks the chart reads, so the hint can never disagree
  // with the plotted line OR the Schläge table. Gated on a Prognose actually
  // being drawn — no hint about a line that is not there. The "erwartetes
  // Saisonende" legend KPI is a partial sum whenever coverage is partial — this
  // hint is the disclosure.
  const coverageHint = useMemo(() => {
    if (!rows.some((r) => r.forecastCum != null)) return null;
    const { covered, harvesting } = forecastCoverage(
      seasonCurrentPicks.map((p) => ({ date: p.date, qty: p.qty, seriesId: p.cropCycleId })),
      forecastPoints,
    );
    return forecastCoverageHintText(covered, harvesting);
  }, [rows, seasonCurrentPicks, forecastPoints]);

  // ── #2459 field-narrowing: the DISPLAYED chart follows the selected Schlag ──
  // A selected Schlag narrows the chart's Ist / Prognose / Plan to that one
  // crop_cycle; the day-readout (``?d=``) reads the SAME ``chartRows`` so field
  // and day compose. The PRIOR-YEAR overlay is field-scoped too (#2505): the
  // same fetched picks re-mapped to the Schlag's own prior cycles via
  // ``field_id`` (``fieldPriorSeries`` above). The HORIZON overlay
  // ("Prognose vor N Tagen") is SUPPRESSED in field view (codex P2): its vintage
  // pool is fetched group/refinement-wide and carries no crop_cycle, so drawing
  // it over a single field would mix group data with field values; per-field is a
  // follow-up (``fieldSelected`` / ``showHorizonOverlay`` are defined above so
  // the vintage fetch + horizon build are skipped in a field view too).
  // ``selectedFieldId == null`` ⇒ ``chartRows`` IS ``rows`` (Gesamt aggregate) —
  // identical to the pre-#2459 behaviour.
  const chartActuals = useMemo(
    () => narrowToCycle(seasonCurrentPicks, selectedFieldId),
    [seasonCurrentPicks, selectedFieldId],
  );
  const chartForecastPoints = useMemo<OutputForecastPoint[]>(
    () =>
      selectedFieldId == null
        ? forecastPoints
        : // #2775-R2 — the field view draws from the same drawable set (an
          // undrawable point must not resurface under a Schlag selection);
          // the untagged (no seriesId) single-cycle mapping is unchanged.
          drawnForecasts
            .filter((f) => f.cropCycleId === selectedFieldId)
            .map((f) => ({ forecastDate: f.forecastDate as string, value: f.value as number, measureKind: f.measureKind })),
    [selectedFieldId, forecastPoints, drawnForecasts],
  );
  const chartPlan = useMemo<OutputPlanTarget | null>(
    () =>
      selectedFieldId == null
        ? plan
        : pickPlanTarget(plans.filter((p) => p.cropCycleId === selectedFieldId), currentSeason),
    [selectedFieldId, plan, plans, currentSeason],
  );
  const chartRows = useMemo(
    () =>
      selectedFieldId == null
        ? rows
        : buildOutputChart({
            actuals: chartActuals.map((p) => ({ date: p.date, qty: p.qty, unit: p.unit })),
            forecasts: chartForecastPoints,
            plan: chartPlan,
            grain,
            // #2459 (codex P2) — no group horizon in a field-scoped chart.
            horizonPoints: undefined,
            window: axisWindow,
            // #2505 — the field's OWN prior years, never the group curve.
            priorYears: fieldPriorSeries,
          }),
    [selectedFieldId, rows, chartActuals, chartForecastPoints, chartPlan, grain, horizonPoints, axisWindow, fieldPriorSeries],
  );

  // #2456 Ask 4 — resolve the ?d= selection against the (possibly field-narrowed)
  // chart rows (the full axis, so a selection scrolled out of the zoom window
  // keeps its readout), and clear it once the bucket leaves the data range (a
  // stale ?d= after a grain switch). Gated on content-ready + non-empty rows so a
  // deep-linked ?d= survives until the data loads; the chart marks it only inside
  // the zoom window. ``chartRows`` shares ``rows``' bucket spine, so narrowing a
  // field only changes the readout's VALUES, never which buckets are in range.
  const selectedRow = useMemo(() => findRowByBucket(chartRows, selectedBucketParam), [chartRows, selectedBucketParam]);
  useEffect(() => {
    if (!selectedBucketParam) return;
    if (!showLensContent || rows.length === 0) return;
    if (rows.some((r) => r.bucket === selectedBucketParam)) return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('d');
        return next;
      },
      { replace: true },
    );
  }, [selectedBucketParam, showLensContent, rows, setSearchParams]);

  // #2459 — strip a stale ``?f=`` once its Schlag leaves the current member set
  // (a crop/variety change clears it via the lens/switch handlers; this covers a
  // deep link or data refresh that orphans the param). Gated like the default-cc
  // stamp so a valid ``?f=`` deep link is never dropped while data is still
  // loading. ``selectedFieldId`` already resolves a stale ``?f=`` to Gesamt, so
  // this only keeps the URL honest — the chart is aggregate either way.
  useEffect(() => {
    if (isLoading || !showLensContent) return;
    if (fParam != null && !currentMemberIds.includes(fParam)) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete('f');
          return next;
        },
        { replace: true },
      );
    }
  }, [fParam, isLoading, showLensContent, currentMemberIds, setSearchParams]);

  // ── ?cc= deep-link state machine (#2408) — ONE banner surface ────────────
  // ``groups`` (never ``visibleGroups``): a cc into a collapsed group resolves.
  const viewState = useMemo(
    () => resolveCycleView(ccParam, cycles, groups, currentSeason),
    [ccParam, cycles, groups, currentSeason],
  );
  // #2419 — pick-aware harvest history: a member at a DRAWABLE prior offset
  // (1..3) that actually has picks. Replaces the membership proxy so the
  // "Vorjahresdaten liegen vor" neutral banner (and its overlay offer) only fire
  // when there is REAL prior harvest to draw — never for a crop whose prior years
  // are register rows without picks. The ``o <= 3`` bound is explicit here: it
  // used to arrive implicitly with the four-WJ span window that #2610 removed,
  // and without it a crop last harvested in 2015 would claim Vorjahresdaten while
  // ``offeredOffsets`` (capped at 1..3) renders no chip to act on.
  const cropHasHistory = useMemo(
    () =>
      selectedGroup?.members.some((m) => {
        const o = offsetOf(m, currentSeason);
        return o != null && o >= 1 && o <= 3 && pickCycles.has(m.cropCycleId);
      }) ?? false,
    [selectedGroup, currentSeason, pickCycles],
  );
  const displayedVerdict = useMemo(
    () => resolveBannerVerdict(verdict, viewState, cropHasHistory),
    [verdict, viewState, cropHasHistory],
  );

  // ── Per-field table (#2373 change #4) ────────────────────────────────────
  // Feed the SAME ``seasonCurrentPicks`` the chart/banner use, so the table and
  // the banner can never disagree about this season's harvest (#2413). Picks
  // arrive already season-sliced — no per-row filter needed here.
  const fieldRows = useMemo(
    () =>
      buildFieldRows({
        members: currentSeasonMembers,
        picks: seasonCurrentPicks,
        // #2775-R2 — the table's Prognose column reads the DRAWN forecast set:
        // a figure the chart cannot draw must not print one card below it.
        forecasts: drawnForecasts,
        plans: plans.filter((p) => p.cropCycleId != null && currentMemberIds.includes(p.cropCycleId)),
        fieldNames,
        currentSeason,
      }),
    [currentSeasonMembers, seasonCurrentPicks, drawnForecasts, plans, currentMemberIds, fieldNames, currentSeason],
  );

  // #2459 — the selected Schlag's display name (from the SAME field rows the
  // table shows), appended to the chart title when a field is selected.
  const selectedSchlag = useMemo(
    () => (selectedFieldId ? fieldRows.find((r) => r.cropCycleId === selectedFieldId)?.schlag ?? null : null),
    [selectedFieldId, fieldRows],
  );
  // The chart-card title (h2). Renders when refined OR a Schlag is selected:
  //   refinement only → "Kultur · Sorte" (unchanged #2408-B)
  //   Schlag only     → "Kultur · Schlag" (#2459)
  //   both            → "Kultur · Sorte · Schlag"
  // At crop total with no field selected none renders — the crop ``<h1>`` header
  // is the title. (The crop header itself stays group-level, #2459 out-of-scope.)
  const chartTitle = useMemo(() => {
    if (!selectedGroup || (refinement == null && selectedSchlag == null)) return null;
    const parts = [selectedGroup.label];
    if (refinement != null) parts.push(refinement.label);
    if (selectedSchlag != null) parts.push(selectedSchlag);
    return parts.join(' · ');
  }, [selectedGroup, refinement, selectedSchlag]);

  // #2505 — what the prior-year UI OFFERS: a field view offers only the
  // Schlag's OWN prior offsets (what its curve can draw), Gesamt the group's.
  // The SINGLE source for the top-right toggle list AND the neutral-banner
  // overlay offer below (codex P2), so the offer can never stamp a ``?py=``
  // the displayed chart cannot draw. A plain ternary over two memoized
  // arrays — identity is stable per render.
  const offeredOffsets = fieldSelected ? fieldAvailableOffsets : availableOffsets;

  // #2436 — concrete calendar-year labels (2025/2024/2023), derived from the
  // current WJ so they roll forward each season, replacing the relative "−N Jahr".
  // The enabled set stays the group ?py= state so a toggle survives
  // Gesamt ⇄ Schlag round-trips unchanged (#2505).
  const priorYearToggles = useMemo<PriorYearToggle[]>(
    () =>
      offeredOffsets.map((o) => ({
        offset: o,
        label: priorYearLabel(currentSeason, o),
        enabled: enabledOffsets.includes(o),
      })),
    [offeredOffsets, enabledOffsets, currentSeason],
  );

  const handleGrainChange = useCallback((g: Grain) => setParams({ g }), [setParams]);
  // #2544-B — the default (Kumuliert) is DELETED from the URL rather than written
  // as ``?m=kumuliert``, matching how ``?h=``/``?f=`` drop when they go back to
  // their default. ``setParams`` preserves every other key by construction.
  const handleModeChange = useCallback(
    (m: ChartMode) => setParams({ m: m === 'perPeriod' ? 'einzel' : null }),
    [setParams],
  );
  /**
   * #2546-B — deliberately the OPPOSITE of ``handleModeChange`` above: both
   * values are written to the URL, including the one that happens to match the
   * current mode's default.
   *
   * That is not redundancy, it is the third state. ``?pm=`` absent means "no
   * choice — follow the data mode", so clearing it on the default would silently
   * demote a real choice back to "follow the mode", and the presentation would
   * flip on the farmer's next Kumuliert ⇄ Einzelernte switch. Writing ``linie``
   * explicitly is the only way to say "Linie, and keep it that way".
   */
  const handlePresentationChange = useCallback(
    (p: ChartPresentation) => setParams({ pm: p }),
    [setParams],
  );
  const handleOverlayToggle = useCallback(
    // #2776 — stamp a horizon the pool can resolve, not a fixed 30 that draws
    // nothing (the whole bug, in one line).
    (on: boolean) => setParams({ h: on ? String(toggleHorizonDays) : null }),
    [setParams, toggleHorizonDays],
  );
  const handleHorizonDaysChange = useCallback((n: number) => setParams({ h: String(n) }), [setParams]);
  const handleTogglePriorYear = useCallback(
    (offset: number) => {
      const next = new Set(priorYearsParam);
      if (next.has(offset)) next.delete(offset);
      else next.add(offset);
      const arr = [...next].sort();
      setParams({ py: arr.length ? arr.join(',') : null });
    },
    [priorYearsParam, setParams],
  );
  // Lens selection (#2408-B): a crop row selects the crop TOTAL (clears ?s=,
  // stamps ?cc=); a sub-item selects a refinement — ?s= carries the raw variety
  // name (or the ~ sentinel for ohne Sorte) and ?cc= is stamped to the refined
  // subset's deterministic first member (coordinator decision: cc-based flows
  // stay coherent). ?g=/?h=/?py= are never touched.
  const handleSelectLens = useCallback(
    (groupKey: string, subItemId: string | null) => {
      const g = groups.find((x) => x.groupKey === groupKey);
      // HOF-106 — an explicit crop selection is the only thing that writes the
      // preference, and it writes the GROUP KEY rather than the cc it stamps: a
      // crop cycle belongs to one harvest season, so a stored id would be dead
      // at the next rollover while „Himbeeren" is not. A sub-item (variety)
      // click arrives here with the same parent key, which is the crop the
      // reader is in either way. Resolution deliberately does not write —
      // merely viewing a ``?cc=`` deep link someone shared is not a choice.
      if (g) writeStoredGroup(tenantId, g.groupKey);
      if (g && subItemId) {
        // #2435 — resolve against the SAME data-presence-filtered axis the lens
        // rendered (fail-open on error included), so a click can never select a
        // hidden (dataless) variety nor miss a fail-open one.
        const r = visibleRefinementSubItems(
          g,
          currentSeason,
          refinementPresence.pick,
          refinementPresence.currentForecast,
        ).find((x) => x.id === subItemId);
        if (r) {
          // #2459 — a crop/variety change resets the field selection to Gesamt.
          setParams({
            cc: firstCycleOfMembers(r.members, currentSeason),
            s: r.variety ?? OHNE_SORTE_SENTINEL,
            f: null,
          });
          return;
        }
      }
      setParams({ cc: g ? firstCycleOfGroup(g, currentSeason) : null, s: null, f: null });
    },
    [groups, currentSeason, refinementPresence, setParams, tenantId],
  );
  // #2474 — when the current ``?cc=`` forces the Planung banner (a future-season
  // deep link) yet the crop HAS current-WJ members (``switchCc`` non-null), the
  // chart/table already render the CURRENT season. Engaging a current Schlag /
  // Gesamt must therefore also drop the now-stale banner. This holds the
  // deterministic current anchor the state machine resolved (``switchCc`` =
  // ``firstCycleOfGroup``'s current-first pick) to stamp onto ``?cc=`` alongside
  // the ``?f=`` update; null in every non-switchable view, where the field
  // handlers leave ``?cc=`` untouched (#2459's group-anchor contract). The anchor
  // is a GROUP anchor, never the clicked field — ``?cc=`` stays ≠ ``?f=``.
  const planungCurrentAnchor = useMemo(
    () => (viewState.kind === 'planung' ? viewState.switchCc : null),
    [viewState],
  );
  // #2459 — select a Schlag (``?f=``) to narrow the chart; select Gesamt to
  // return to the group aggregate (clears ``?f=``). ``?cc=`` (the group anchor)
  // is left untouched — the field lives inside the already-anchored group —
  // EXCEPT from a forced-Planung view (#2474): re-anchor ``?cc=`` to the current
  // season so the stale banner exits alongside the ``?f=`` change.
  const handleSelectField = useCallback(
    (cropCycleId: string) =>
      setParams(planungCurrentAnchor != null ? { f: cropCycleId, cc: planungCurrentAnchor } : { f: cropCycleId }),
    [setParams, planungCurrentAnchor],
  );
  const handleSelectTotal = useCallback(
    () => setParams(planungCurrentAnchor != null ? { f: null, cc: planungCurrentAnchor } : { f: null }),
    [setParams, planungCurrentAnchor],
  );
  // #2544-B — is a Plan LINE on the plot right now? A plan is a season scalar
  // with no per-bucket value, so ``buildChartSeries`` drops the series entirely
  // in Einzelernte; the "Ausgewählter Tag" readout lists exactly the visible
  // series, so it has to follow the same rule.
  const planLineDrawn = chartPlan != null && mode === 'cumulative';

  // #2456 Ask 4 — click a chart point → persist the bucket; reset clears it.
  const handleSelectBucket = useCallback((bucket: string) => setParams({ d: bucket }), [setParams]);
  const handleResetSelection = useCallback(() => setParams({ d: null }), [setParams]);

  // #2407 Gap 2 (+ #2408 prior-framing) — a neutral banner ("Kein Vergleich",
  // the WJ-scoped no-harvest framing, a prior-framing Planung) is a dead end
  // when prior-year overlays could contextualise it: offer them whenever
  // prior-year MEMBERS exist and no overlay is on. Membership is a proxy
  // (picks are lazy — unknowable lens-wide), so the copy promises the option,
  // not data; stamping exactly ``offeredOffsets`` keeps ``?py=`` in sync
  // with what the top-right toggles show — INCLUDING a field view (#2505
  // codex P2): a Schlag without prior data gets no offer, never a click that
  // stamps a ``?py=`` its chart cannot draw. URL state only — deep-linkable.
  const handleEnableAllPriorYears = useCallback(
    () => setParams({ py: offeredOffsets.join(',') }),
    [setParams, offeredOffsets],
  );
  const overlayOffer =
    displayedVerdict.level === 'neutral' && offeredOffsets.length > 0 && enabledOffsets.length === 0 ? (
      <span className="ernteprognose-verdict__offer">
        Vorjahreszeiträume können zum Vergleich eingeblendet werden.
        <button
          type="button"
          className="ernteprognose-verdict__offer-button"
          onClick={handleEnableAllPriorYears}
        >
          Vorjahres-Kurven einblenden
        </button>
      </span>
    ) : undefined;

  // #2408 — the Planung banner's one-click switch onto the crop's current-WJ
  // view: stamps ``?cc=`` to the deterministic current member (the state
  // machine resolved it), which also moves the table drill onto a live row.
  // #2408-B (codex r1+r2): the switch is refinement-aware. A refined subset
  // with its OWN current-WJ member switches within the refinement (its
  // deterministic first member — ``firstCycleOfMembers`` ranks current-WJ
  // first), never jumping the farmer to another Sorte. Only a refinement that
  // cannot represent the current season (future-only Sorte) falls back to the
  // crop-level target AND clears ``?s=`` — otherwise the subset re-stamp
  // bounces ``?cc=`` straight back to the future member (no-op loop).
  const handleSwitchToCurrent = useCallback(() => {
    if (viewState.kind !== 'planung' || viewState.switchCc == null) return;
    const refinedHasCurrent = refinement?.members.some((m) => offsetOf(m, currentSeason) === 0) ?? false;
    if (refinement && refinedHasCurrent) {
      // #2459 — the switch moves the crop context, so reset the field selection.
      setParams({ cc: firstCycleOfMembers(refinement.members, currentSeason), f: null });
      return;
    }
    setParams({ cc: viewState.switchCc, s: null, f: null });
  }, [viewState, refinement, currentSeason, setParams]);
  // #2610 — the switch is SUPPRESSED until the pick-presence aggregate settles.
  // ``currentSeason`` can still be promoted by that aggregate, and a ``?cc=``
  // deep link renders the page before it resolves (``hasDeepLinkGroup`` bypasses
  // the loading state by design). Offering "Zur laufenden Saison wechseln"
  // against a not-yet-promoted season would let one click pin the farmer to the
  // PRIOR season moments before the running one becomes current. The banner text
  // still renders — only the irreversible action waits.
  const switchAction =
    pickPresenceStatus !== 'loading' && viewState.kind === 'planung' && viewState.switchCc != null ? (
      <span className="ernteprognose-verdict__offer">
        <button
          type="button"
          className="ernteprognose-verdict__offer-button"
          onClick={handleSwitchToCurrent}
        >
          Zur laufenden Saison wechseln
        </button>
      </span>
    ) : undefined;

  return (
    <div className="ernteprognose-page">
      <LensePanel
        title="Ernteprognose"
        items={lensItems}
        selectedItem={selectedGroupKey ?? undefined}
        selectedSubItem={refinement?.id ?? null}
        onSelect={handleSelectLens}
        isMobileOpen={mobilePanel?.isOpen ?? false}
        onMobileClose={() => mobilePanel?.close()}
      />
      <PageLoadOverlay visible={pageLoadOverlay.showOverlay} elapsedSeconds={pageLoadOverlay.elapsedSeconds} />
      <div className="ernteprognose-page__main">
        {!isLoading && error != null && (
          <div className="ernteprognose-page__error" role="status">
            Hinweis: Einige Daten konnten nicht geladen werden.
          </div>
        )}

        {!isLoading && error == null && groups.length === 0 && (
          <div className="ernteprognose-page__empty" role="status">
            Noch keine Kulturen erfasst — sobald Anbau, Prognose oder Ernte vorliegen, erscheint hier
            deine Ertragsprognose.
          </div>
        )}

        {!isLoading && error == null && groups.length > 0 && showLensNotice && (
          <div className="ernteprognose-page__error" role="status">
            Hinweis: Kulturen mit Erntedaten konnten nicht geprüft werden — es werden vorläufig alle Kulturen angezeigt.
          </div>
        )}

        {!isLoading && error == null && groups.length > 0 && showPickLoading && (
          <div className="ernteprognose-page__empty" role="status">
            Erntedaten werden geladen …
          </div>
        )}

        {!isLoading && error == null && groups.length > 0 && showEmptyLens && (
          <div className="ernteprognose-page__empty" role="status">
            Keine Kulturen mit Erntedaten — sobald Prognose, Plan oder Ernte für eine Kultur vorliegen, erscheint sie
            hier.
          </div>
        )}

        {!isLoading && error == null && groups.length > 0 && showLensContent && (
          <>
            <header className="ernteprognose-page__header">
              <h1 className="ernteprognose-page__title">
                {selectedGroup ? cropTotalHeader(selectedGroup.label, selectedGroup.currentWjCount) : 'Ertragsprognose'}
              </h1>
              {/* #2544-A — the Zeitraster (1T/1W/1M/1J) segmented control used to
                  sit here. It now lives in the chart's own toolbar beside the zoom
                  controls: chart granularity belongs to the chart, and the issue's
                  follow-up is explicit that there must be ONE control, not two. */}
              <div className="ernteprognose-page__controls">
                <label className="ernteprognose-page__overlay-toggle">
                  <input
                    type="checkbox"
                    checked={overlayEnabled}
                    disabled={horizonUnavailable}
                    title={horizonUnavailable ? HORIZON_UNAVAILABLE_TITLE : undefined}
                    onChange={(e) => handleOverlayToggle(e.target.checked)}
                  />
                  Prognose vor
                  <select
                    aria-label="Horizont in Tagen"
                    value={String(horizonDays ?? toggleHorizonDays)}
                    disabled={!overlayEnabled}
                    onChange={(e) => handleHorizonDaysChange(Number(e.target.value))}
                  >
                    {/* #2776 — the ladder always renders WHOLE: an unresolvable
                        step is ``disabled``, not dropped, so a deep-linked
                        ``?h=90`` still shows as the current value and the
                        farmer can see which lookbacks exist at all. */}
                    {HORIZON_LADDER.map((n) => (
                      <option key={n} value={n} disabled={!selectableHorizons.includes(n)}>
                        {n}
                      </option>
                    ))}
                  </select>
                  Tagen
                </label>
              </div>
            </header>

            <OutputVerdictLine verdict={displayedVerdict} action={switchAction ?? overlayOffer} />

            {wjStartMonthSource === 'default' && (
              // #2601-B — never silently reshape the chart around an assumed
              // Wirtschaftsjahr: say so, in the existing hint style.
              <p className="ernteprognose-page__hint" role="note">
                {wjStartHintText(wjStartMonth)}
              </p>
            )}

            {selectedGroup?.ohneSorte != null && selectedGroup.ohneSorte.currentWjCount > 0 && (
              // Data-quality hint (#2408-B, #2322-class cause): current-WJ
              // Schläge without a Sorte exist. Crop-scoped — stays visible
              // under any refinement; planning-only remainders don't nag.
              <p className="ernteprognose-page__hint" role="note">
                {ohneSorteHintText(selectedGroup.ohneSorte.currentWjCount)}
              </p>
            )}

            {selectedFieldId == null && coverageHint != null && (
              // #2775 — partial forecast coverage: the aggregate Prognose (and
              // its "erwartetes Saisonende" legend KPI) sums increments for
              // only SOME harvesting Schläge; disclose rather than silently
              // under-estimate. Suppressed in a field view (horizon-note
              // precedent): the hint describes the AGGREGATE line, which a
              // field-scoped chart does not draw.
              <p className="ernteprognose-page__hint" role="note">
                {coverageHint}
              </p>
            )}

            <section className="ernteprognose-page__chart-card">
              {chartTitle != null && (
                // Chart title reflects the ACTIVE refinement (#2408-B) and/or the
                // selected Schlag (#2459); at crop total with no field selected the
                // crop header above is the title, so none renders.
                <h2 className="ernteprognose-page__chart-title">{chartTitle}</h2>
              )}
              <OutputSeriesChart
                rows={chartRows}
                unit={unit}
                grain={grain}
                onGrainChange={handleGrainChange}
                mode={mode}
                onModeChange={handleModeChange}
                presentation={presentation}
                onPresentationChange={handlePresentationChange}
                showHorizon={showHorizonOverlay}
                horizonDays={horizonDays ?? DEFAULT_HORIZON_DAYS}
                hasPlan={chartPlan != null}
                priorYears={priorYearToggles}
                onTogglePriorYear={handleTogglePriorYear}
                selectedBucket={selectedRow ? selectedBucketParam : null}
                onSelectBucket={handleSelectBucket}
              />
              <SelectedDayReadoutPanel
                row={selectedRow}
                grain={grain}
                unit={unit}
                options={{
                  showHorizon: showHorizonOverlay,
                  horizonDays: horizonDays ?? DEFAULT_HORIZON_DAYS,
                  // #2544-B — NOTE the different meaning from the chart's own
                  // ``hasPlan`` above: that one says "a plan exists" and
                  // ``buildChartSeries`` applies the mode rule itself, while the
                  // readout lists exactly the series VISIBLE on the plot, so it
                  // needs "a Plan line is actually drawn". Named, not inlined,
                  // so the two can't be swapped by accident.
                  hasPlan: planLineDrawn,
                  priorYears: priorYearToggles,
                }}
                onReset={handleResetSelection}
              />
              {chartPlan == null && (
                <p className="ernteprognose-page__note">
                  Kein Plan hinterlegt — Prognose und Ist werden ohne Plan-Ziel angezeigt.
                </p>
              )}
              {overlayEnabled && fieldSelected && (
                // #2459 (codex P2) — the horizon overlay is a group-level view; a
                // field-scoped chart hides it rather than show group vintage data.
                <p className="ernteprognose-page__note">
                  Der Prognose-Rückblick („Prognose vor N Tagen“) ist nur in der Gesamtansicht verfügbar.
                </p>
              )}
              {showHorizonOverlay && !vintagesLoading && horizonPoints != null && horizonPoints.length === 0 && (
                // #2776 — the overlay is ON but no vintage is old enough to
                // reconstruct it: an empty chart must never be the only
                // feedback. Carries the pool's CONCRETE facts (earliest Stand,
                // max lead) and the one action that helps, instead of silently
                // rewriting the deep-linked ``?h=``. Gated on the fetch having
                // settled so it cannot flash during a 400-call fan-out, and on
                // ``showHorizonOverlay`` so it can never double-render with the
                // field-view note above. Distinct from the truncation note
                // below — that one is the 400-entity page cap, nothing else.
                <p className="ernteprognose-page__note">
                  {horizonEmptyNoteText(
                    horizonDays ?? DEFAULT_HORIZON_DAYS,
                    vintages,
                    exactHorizons.some((n) => n < (horizonDays ?? DEFAULT_HORIZON_DAYS)),
                  )}
                </p>
              )}
              {showHorizonOverlay && vintagesTruncated && (
                <p className="ernteprognose-page__note">
                  Für diese Kultur werden nur die ersten Prognose-Tage im Rückblick berücksichtigt.
                </p>
              )}
            </section>

            <section className="ernteprognose-page__table-card">
              {/* #2610 — name the concrete season ("Saison 2026"), the
                  Deckungsbeitrag labelling precedent, instead of the
                  Wirtschaftsjahr frame the table no longer slices by. */}
              <h2 className="ernteprognose-page__table-title">Schläge — {saisonLabel(currentSeason)}</h2>
              <OutputFieldTable
                rows={fieldRows}
                selectedId={selectedFieldId}
                onSelect={handleSelectField}
                onSelectTotal={handleSelectTotal}
              />
            </section>
          </>
        )}
      </div>
    </div>
  );
}
